/**
 * Contract Version History
 * Manages encrypted contract version snapshots with dual-copy encryption.
 * Each version stores two encrypted copies (one per partner's key).
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  updateDoc,
  deleteField,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { ClientEncryption, shouldEncrypt } from './encryption-utils';
import { getEncryptionKey } from './auth';
import { getRelationship, getJointResponses, getUserResponses, getJointComfortLevel, type QuestionResponse } from './storage';
import { getSignedContract, type Signature } from './signing';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface VersionMetadata {
  answeredQuestionIds: string[];
  privateQuestionIds: { partner1: string[]; partner2: string[] };
  privateSections: { partner1: string[]; partner2: string[] };
  comfortLevel: number;
  articleTitles: string[];
}

export interface SignatureSnapshot {
  partner1: { signatureType: 'drawn' | 'typed'; signedAt: string } | null;
  partner2: { signatureType: 'drawn' | 'typed'; signedAt: string } | null;
}

export interface ContractVersionSummary {
  versionId: string;
  versionNumber: number;
  finalizedAt: string;
  metadata: VersionMetadata;
  signatures: SignatureSnapshot;
  hasMyEncryptedCopy: boolean;
  hasPendingEncryption: boolean;
}

interface EncryptedPartnerCopy {
  contractContent: string; // AES-256-GCM base64
  userId: string;
}

export interface ContractVersionDoc {
  versionId: string;
  relationshipId: string;
  versionNumber: number;
  partner1Encrypted: EncryptedPartnerCopy | null;
  partner2Encrypted: EncryptedPartnerCopy | null;
  pendingEncryption: string | null;
  metadata: VersionMetadata;
  signatures: SignatureSnapshot;
  finalizedAt: Timestamp | string;
}

// ─── Create Version ──────────────────────────────────────────────────────────

/**
 * Create a new contract version at finalization time.
 * Encrypts with the current user's key and stores a pendingEncryption
 * copy for the absent partner.
 */
export async function createContractVersion(
  relationshipId: string,
  userId: string,
  contractContent: string,
  metadata: VersionMetadata,
  signaturesSnapshot: SignatureSnapshot
): Promise<string> {
  const encryptionKey = getEncryptionKey();
  const relationship = await getRelationship(relationshipId);
  if (!relationship) throw new Error('Relationship not found');

  const isPartner1 = relationship.partner1Id === userId;

  let partner1Encrypted: EncryptedPartnerCopy | null = null;
  let partner2Encrypted: EncryptedPartnerCopy | null = null;
  let pendingEncryption: string | null = null;

  if (encryptionKey && shouldEncrypt()) {
    const encrypted = await ClientEncryption.encrypt(contractContent, encryptionKey);

    if (isPartner1) {
      partner1Encrypted = { contractContent: encrypted, userId: relationship.partner1Id };
    } else {
      partner2Encrypted = { contractContent: encrypted, userId: relationship.partner2Id! };
    }

    // Store plaintext temporarily for the absent partner to encrypt later
    pendingEncryption = contractContent;
  } else {
    // Encryption disabled — store plaintext in both slots
    partner1Encrypted = { contractContent, userId: relationship.partner1Id };
    if (relationship.partner2Id) {
      partner2Encrypted = { contractContent, userId: relationship.partner2Id };
    }
  }

  // Get current version count and increment
  const currentCount = relationship.contractVersionCount || 0;
  const newVersionNumber = currentCount + 1;

  // Write version document
  const versionsCol = collection(db, 'contractVersions', relationshipId, 'versions');
  const versionRef = doc(versionsCol);

  await setDoc(versionRef, {
    versionId: versionRef.id,
    relationshipId,
    versionNumber: newVersionNumber,
    partner1Encrypted,
    partner2Encrypted,
    ...(pendingEncryption !== null && { pendingEncryption }),
    metadata,
    signatures: signaturesSnapshot,
    createdAt: serverTimestamp(),
    finalizedAt: new Date().toISOString(),
  });

  // Update version count on relationship
  const relationshipRef = doc(db, 'relationships', relationshipId);
  await updateDoc(relationshipRef, {
    contractVersionCount: newVersionNumber,
  });

  return versionRef.id;
}

// ─── List Versions ───────────────────────────────────────────────────────────

/**
 * List all contract versions for a relationship (metadata only, no encrypted content).
 */
export async function listContractVersions(
  relationshipId: string,
  userId: string
): Promise<ContractVersionSummary[]> {
  const versionsCol = collection(db, 'contractVersions', relationshipId, 'versions');
  const q = query(versionsCol, orderBy('versionNumber', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();

    // Determine which partner slot belongs to the current user
    const isPartner1Copy = data.partner1Encrypted?.userId === userId;
    const isPartner2Copy = data.partner2Encrypted?.userId === userId;
    const hasMyEncryptedCopy = isPartner1Copy || isPartner2Copy;

    return {
      versionId: data.versionId,
      versionNumber: data.versionNumber,
      finalizedAt: data.finalizedAt,
      metadata: data.metadata,
      signatures: data.signatures,
      hasMyEncryptedCopy,
      hasPendingEncryption: !!data.pendingEncryption,
    };
  });
}

// ─── Get & Decrypt Version ──────────────────────────────────────────────────

/**
 * Get a single contract version document.
 */
export async function getContractVersion(
  relationshipId: string,
  versionId: string
): Promise<ContractVersionDoc | null> {
  const versionRef = doc(db, 'contractVersions', relationshipId, 'versions', versionId);
  const snap = await getDoc(versionRef);
  return snap.exists() ? (snap.data() as ContractVersionDoc) : null;
}

/**
 * Decrypt the contract content from a version for the current user.
 * Returns null if decryption fails or no encrypted copy exists.
 */
export async function decryptVersionContent(
  versionData: ContractVersionDoc,
  userId: string
): Promise<string | null> {
  const encryptionKey = getEncryptionKey();

  // Determine which copy belongs to this user
  const isPartner1 = versionData.partner1Encrypted?.userId === userId;
  const isPartner2 = versionData.partner2Encrypted?.userId === userId;

  const myCopy = isPartner1
    ? versionData.partner1Encrypted
    : isPartner2
      ? versionData.partner2Encrypted
      : null;

  if (!myCopy) {
    // No encrypted copy for this user — check pendingEncryption
    if (versionData.pendingEncryption) {
      return versionData.pendingEncryption;
    }
    return null;
  }

  // If encryption is disabled or no key, content might be plaintext
  if (!shouldEncrypt() || !encryptionKey) {
    return myCopy.contractContent;
  }

  try {
    return await ClientEncryption.decrypt(myCopy.contractContent, encryptionKey);
  } catch (error) {
    console.error('Failed to decrypt contract version:', error);
    return null;
  }
}

// ─── Complete Pending Encryption ─────────────────────────────────────────────

/**
 * Encrypt pending versions for the current user (deferred encryption).
 * Called when a partner logs in and there are versions missing their encrypted copy.
 * Returns the count of versions that were completed.
 */
export async function completePendingEncryption(
  relationshipId: string,
  userId: string
): Promise<number> {
  const encryptionKey = getEncryptionKey();
  if (!encryptionKey || !shouldEncrypt()) return 0;

  const relationship = await getRelationship(relationshipId);
  if (!relationship) return 0;

  const isPartner1 = relationship.partner1Id === userId;
  const myField = isPartner1 ? 'partner1Encrypted' : 'partner2Encrypted';

  const versionsCol = collection(db, 'contractVersions', relationshipId, 'versions');
  const snapshot = await getDocs(versionsCol);

  let completedCount = 0;

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();

    // Check if this version is missing my copy AND has pendingEncryption
    if (!data[myField] && data.pendingEncryption) {
      try {
        const encrypted = await ClientEncryption.encrypt(data.pendingEncryption, encryptionKey);

        await updateDoc(docSnap.ref, {
          [myField]: { contractContent: encrypted, userId },
          pendingEncryption: deleteField(),
        });

        completedCount++;
      } catch (error) {
        console.error(`Failed to complete encryption for version ${data.versionId}:`, error);
      }
    }
  }

  return completedCount;
}

// ─── Metadata Collection ─────────────────────────────────────────────────────

/**
 * Collect version metadata from the current relationship state.
 * Called at finalization time to capture what was answered and what was private.
 */
export async function collectVersionMetadata(
  relationshipId: string,
  articleTitles: string[]
): Promise<VersionMetadata> {
  // Joint responses — get answered question IDs
  const jointResponses = await getJointResponses(relationshipId);
  const answeredQuestionIds = Object.keys(jointResponses);

  // Individual privacy data for both partners
  const relationship = await getRelationship(relationshipId);
  let partner1PrivateQuestionIds: string[] = [];
  let partner2PrivateQuestionIds: string[] = [];
  let partner1PrivateSections: string[] = [];
  let partner2PrivateSections: string[] = [];

  if (relationship) {
    try {
      const p1Responses = await getUserResponses(relationshipId, relationship.partner1Id);
      partner1PrivateQuestionIds = Object.entries(p1Responses.responses || {})
        .filter(([, r]: [string, QuestionResponse]) => r.isPrivate)
        .map(([qId]) => qId);
      partner1PrivateSections = p1Responses.privateSections || [];
    } catch {
      // Partner 1 responses may not exist yet
    }

    if (relationship.partner2Id) {
      try {
        const p2Responses = await getUserResponses(relationshipId, relationship.partner2Id);
        partner2PrivateQuestionIds = Object.entries(p2Responses.responses || {})
          .filter(([, r]: [string, QuestionResponse]) => r.isPrivate)
          .map(([qId]) => qId);
        partner2PrivateSections = p2Responses.privateSections || [];
      } catch {
        // Partner 2 responses may not exist yet
      }
    }
  }

  const comfortLevel = await getJointComfortLevel(relationshipId);

  return {
    answeredQuestionIds,
    privateQuestionIds: {
      partner1: partner1PrivateQuestionIds,
      partner2: partner2PrivateQuestionIds,
    },
    privateSections: {
      partner1: partner1PrivateSections,
      partner2: partner2PrivateSections,
    },
    comfortLevel,
    articleTitles,
  };
}

/**
 * Collect signature metadata from the current signed contract.
 */
export async function collectSignatureSnapshot(
  relationshipId: string
): Promise<SignatureSnapshot> {
  const signedContract = await getSignedContract(relationshipId);

  if (!signedContract) {
    return { partner1: null, partner2: null };
  }

  const toSnapshot = (sig: Signature | null | undefined) => {
    if (!sig) return null;
    return {
      signatureType: sig.signatureType,
      signedAt: sig.signedAt instanceof Timestamp
        ? sig.signedAt.toDate().toISOString()
        : String(sig.signedAt),
    };
  };

  return {
    partner1: toSnapshot(signedContract.signatures.partner1),
    partner2: toSnapshot(signedContract.signatures.partner2),
  };
}
